import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { investorDashboardAPI, formatApiError, resolveImageUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  ChartLineUp, ArrowLeft, Copy, Check, TrendUp, CurrencyDollar,
  Percent, ShareNetwork, QrCode, WhatsappLogo, TwitterLogo,
  FacebookLogo, ChatCircle, Envelope, CaretDown, CaretUp, LinkSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function InvestorDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const { data: res } = await investorDashboardAPI.getOverview();
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

  const getRefLink = (refCode, productId) => `${window.location.origin}/product/${productId}?ref=${refCode}`;

  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(id);
      toast.success('Link copied!');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const getShareLinks = (refLink, productName) => {
    const text = `Check out "${productName}" on Divispend! ${refLink}`;
    return [
      { name: 'WhatsApp', icon: WhatsappLogo, color: '#25D366', url: `https://wa.me/?text=${encodeURIComponent(text)}` },
      { name: 'Twitter', icon: TwitterLogo, color: '#1DA1F2', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}` },
      { name: 'Facebook', icon: FacebookLogo, color: '#1877F2', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refLink)}` },
      { name: 'SMS', icon: ChatCircle, color: '#FF9500', url: `sms:?body=${encodeURIComponent(text)}` },
      { name: 'Email', icon: Envelope, color: '#999999', url: `mailto:?subject=${encodeURIComponent(`Check out ${productName}`)}&body=${encodeURIComponent(text)}` },
    ];
  };

  if (loading) {
    return <div className="min-h-screen bg-[#111111] flex items-center justify-center"><div className="animate-pulse text-[#999999]">Loading...</div></div>;
  }

  const summary = data?.summary || {};
  const investments = data?.investments || [];

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2"><img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" /></Link>
          <Link to="/dashboard"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm"><ArrowLeft className="w-4 h-4 mr-2" />Dashboard</Button></Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <ChartLineUp className="w-7 h-7 text-[#22C55E]" weight="fill" />
            Investor Dashboard
          </h1>
          <p className="text-[#999999] mt-1">Track your investments and help drive sales with your referral links</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
          {[
            { label: 'Invested', value: formatCurrency(summary.total_invested), color: '#22C55E', icon: CurrencyDollar },
            { label: 'Earned', value: formatCurrency(summary.total_earned), color: '#22C55E', icon: TrendUp },
            { label: 'ROI', value: `${summary.roi_percentage || 0}%`, color: '#22C55E', icon: Percent },
            { label: 'Active', value: summary.active, color: '#22C55E', icon: ChartLineUp },
            { label: 'Completed', value: summary.completed, color: '#22C55E', icon: Check },
          ].map(s => (
            <Card key={s.label} className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid={`stat-${s.label.toLowerCase()}`}>
              <CardContent className="p-4 text-center">
                <s.icon className="w-5 h-5 mx-auto mb-1" style={{ color: s.color }} />
                <p className="text-xl font-bold text-white">{s.value}</p>
                <p className="text-[10px] text-[#999999] uppercase tracking-wider mt-0.5">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Investments */}
        {investments.length === 0 ? (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
            <CardContent className="p-8 text-center">
              <ChartLineUp className="w-12 h-12 mx-auto mb-3 text-[#333333]" />
              <p className="text-[#999999]">No investments yet</p>
              <Link to="/explore"><Button size="sm" className="mt-3 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Browse Marketplace</Button></Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="investments-list">
            {investments.map((inv) => {
              const isExpanded = expandedId === inv.investment_id;
              const refLink = getRefLink(inv.ref_code, inv.product_id);
              const shareLinks = getShareLinks(refLink, inv.product_name || inv.product?.name);
              const progress = inv.amount_invested > 0 ? Math.min(100, (inv.total_earned / inv.amount_invested) * 100) : 0;

              return (
                <Card key={inv.investment_id} className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none overflow-hidden" data-testid={`investment-${inv.investment_id}`}>
                  <CardContent className="p-0">
                    {/* Summary Row */}
                    <div
                      className="p-5 cursor-pointer hover:bg-[#151515] transition-colors"
                      onClick={() => setExpandedId(isExpanded ? null : inv.investment_id)}
                    >
                      <div className="flex items-center gap-4">
                        {inv.product?.image_url ? (
                          <img src={resolveImageUrl(inv.product.image_url)} alt="" className="w-14 h-14 rounded-sm object-cover shrink-0" onError={(e) => { e.target.onerror = null; e.target.className = 'hidden'; }} />
                        ) : (
                          <div className="w-14 h-14 rounded-sm bg-[#22C55E]/10 flex items-center justify-center shrink-0">
                            <ChartLineUp className="w-6 h-6 text-[#22C55E]" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Link to={`/product/${inv.product_id}`} className="font-bold text-white text-sm hover:text-[#22C55E] transition-colors truncate" onClick={(e) => e.stopPropagation()}>
                              {inv.product_name || inv.product?.name}
                            </Link>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${inv.status === 'active' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#22C55E]/10 text-[#22C55E]'}`}>
                              {inv.status === 'active' ? 'Active' : 'Completed'}
                            </span>
                          </div>
                          <p className="text-xs text-[#999999]">by {inv.seller?.name} {inv.seller?.username ? `($${inv.seller.username})` : ''}</p>
                        </div>
                        <div className="hidden sm:flex items-center gap-6 shrink-0">
                          <div className="text-right">
                            <p className="text-sm font-bold text-[#22C55E]">{inv.percentage}%</p>
                            <p className="text-[10px] text-[#999999]">per sale</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-[#22C55E]">{formatCurrency(inv.total_earned)}</p>
                            <p className="text-[10px] text-[#999999]">of {formatCurrency(inv.amount_invested)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-white">{inv.referral_stats?.sales_driven || 0}</p>
                            <p className="text-[10px] text-[#999999]">your sales</p>
                          </div>
                        </div>
                        {isExpanded ? <CaretUp className="w-4 h-4 text-[#666666] shrink-0" /> : <CaretDown className="w-4 h-4 text-[#666666] shrink-0" />}
                      </div>

                      {/* Progress bar */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-[10px] mb-1">
                          <span className="text-[#999999]">Earnings: {formatCurrency(inv.total_earned)} / {formatCurrency(inv.amount_invested)}</span>
                          <span className="text-[#22C55E] font-semibold">{progress.toFixed(0)}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-[#333333] rounded-full overflow-hidden">
                          <div className="h-full bg-[#22C55E] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    </div>

                    {/* Expanded Marketing Tools */}
                    {isExpanded && (
                      <div className="border-t border-[#333333] p-5 bg-[#0D1520] animate-in slide-in-from-top-2 duration-200">
                        {/* Mobile stats */}
                        <div className="sm:hidden grid grid-cols-3 gap-3 mb-5">
                          <div className="bg-[#111111] rounded-sm p-3 text-center">
                            <p className="text-sm font-bold text-[#22C55E]">{inv.percentage}%</p>
                            <p className="text-[10px] text-[#999999]">Per sale</p>
                          </div>
                          <div className="bg-[#111111] rounded-sm p-3 text-center">
                            <p className="text-sm font-bold text-[#22C55E]">{formatCurrency(inv.total_earned)}</p>
                            <p className="text-[10px] text-[#999999]">Earned</p>
                          </div>
                          <div className="bg-[#111111] rounded-sm p-3 text-center">
                            <p className="text-sm font-bold text-white">{inv.referral_stats?.sales_driven || 0}</p>
                            <p className="text-[10px] text-[#999999]">Your sales</p>
                          </div>
                        </div>

                        {/* Referral Link */}
                        <div className="mb-5">
                          <div className="flex items-center gap-2 mb-2">
                            <LinkSimple className="w-4 h-4 text-[#22C55E]" />
                            <span className="text-xs font-semibold text-white uppercase tracking-wider">Your Referral Link</span>
                          </div>
                          <div className="flex gap-2">
                            <input
                              readOnly
                              value={refLink}
                              className="flex-1 bg-[#111111] border border-[#333333] rounded-sm px-3 py-2 text-sm text-white font-mono truncate"
                              data-testid={`ref-link-${inv.investment_id}`}
                            />
                            <Button
                              onClick={() => handleCopy(refLink, inv.investment_id)}
                              className={`rounded-sm shrink-0 ${copiedCode === inv.investment_id ? 'bg-[#22C55E] text-black' : 'bg-[#333333] hover:bg-[#444444] text-white'}`}
                              data-testid={`copy-ref-${inv.investment_id}`}
                            >
                              {copiedCode === inv.investment_id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                          <p className="text-[10px] text-[#666666] mt-1">Share this link — when someone buys through it, the sale is tracked to you.</p>
                        </div>

                        {/* Share Buttons */}
                        <div className="mb-5">
                          <div className="flex items-center gap-2 mb-2">
                            <ShareNetwork className="w-4 h-4 text-[#22C55E]" />
                            <span className="text-xs font-semibold text-white uppercase tracking-wider">Share & Promote</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {shareLinks.map((link) => (
                              <a
                                key={link.name}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-medium transition-colors"
                                style={{ backgroundColor: `${link.color}15`, color: link.color }}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`share-${link.name.toLowerCase()}-${inv.investment_id}`}
                              >
                                <link.icon className="w-3.5 h-3.5" weight="fill" />
                                {link.name}
                              </a>
                            ))}
                          </div>
                        </div>

                        {/* QR Code */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <QrCode className="w-4 h-4 text-[#22C55E]" />
                            <span className="text-xs font-semibold text-white uppercase tracking-wider">QR Code</span>
                          </div>
                          <div className="flex items-start gap-4">
                            <div className="bg-white p-2 rounded-lg">
                              <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(refLink)}&bgcolor=FFFFFF&color=111111`}
                                alt="Referral QR"
                                className="w-[120px] h-[120px]"
                                data-testid={`qr-${inv.investment_id}`}
                              />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-[#999999] mb-2">Print this QR code or share it digitally. Anyone who scans it will land on the product page with your referral tracked.</p>
                              <p className="text-xs text-[#666666]">Code: <span className="text-[#22C55E] font-mono font-bold">{inv.ref_code}</span></p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
